/* eslint-disable */
import React, {useEffect, useMemo, useState} from 'react';
import {IEmailCollectionContent} from '../../../types/cards';
import MarketingConsentDialog from './MarketingConsentDialog';
import './EmailCollectionContent.css';
import {useFunnelStore} from "../../../store/useFunnelStore";
import {useTranslation} from "react-i18next";
import {pushInfoToGoogleData} from "../../../utils/googleTag";
import {quizService} from "../../../api/quizService";
import {facebookEventFire} from "../../../utils/facebookEventFire";

interface EmailCollectionProps {
  content: IEmailCollectionContent;
  stepIndex: number;
  onSelect: (email: string, termsConsent: boolean, marketingConsent: boolean) => void;
  step: any;
}

export const EmailCollectionContent: React.FC<EmailCollectionProps> = ({content, onSelect, step, stepIndex}) => {
  const {t} = useTranslation()
  const {updateMailStatus, updateCheckoutField} = useFunnelStore();
  const [email, setEmail] = useState('');
  const [termsConsent, setTermsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showMarketingDialog, setShowMarketingDialog] = useState(false);


  useEffect(() => {
    pushInfoToGoogleData({
      event: 'email_page_view',
      data: {
        stepId: step.stepId,
        stepIndex: stepIndex,
        cardTitle: step.title,
      }
    });
    facebookEventFire('EmailPageView',
      {
        customData: {stepId: step.stepId, stepIndex: stepIndex, cardTitle: step.title},
        userData: {},
      })
  }, [])

  // Validation logic to determine if form is valid
  const isFormValid = useMemo(() => {
    if (content.showCheckboxes === false) {
      return email.trim() !== '';
    }
    return email.trim() !== '' && termsConsent;
  }, [email, termsConsent, content.showCheckboxes]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if(content.showCheckboxes === false){
      quizService.saveUserEmail(email, true)
    }else {
      quizService.saveUserEmail(email, marketingConsent)
    }


    // Only check terms consent if checkboxes are shown
    if (content.showCheckboxes !== false && !termsConsent) {
      setEmailError('You must accept the terms and conditions');
      return;
    }

    setEmailError('');

    // Save email to checkout data for prefilling checkout form
    updateCheckoutField('email', email);

    // Only show marketing dialog if checkboxes are shown and marketing consent is not given
    if (content.showCheckboxes !== false && !marketingConsent) {
      setShowMarketingDialog(true);
      return;
    }
    // addChoice({
    //     stepId: step.stepId,
    //     stepIndex: step.stepIndex,
    //     stepTitle: step.title,
    //     email,
    //     termsConsent,
    //     marketingConsent
    // }
    // When checkboxes are hidden, assume both terms and marketing consent are true
    const finalTermsConsent = content.showCheckboxes === false ? true : termsConsent;
    const finalMarketingConsent = content.showCheckboxes === false ? true : marketingConsent;

    updateMailStatus(finalMarketingConsent)
    onSelect(email, finalTermsConsent, finalMarketingConsent);
  };

  const handleMarketingConfirm = () => {
    setMarketingConsent(true);
    setShowMarketingDialog(false);
    // Save email to checkout data for prefilling checkout form
    updateCheckoutField('email', email);
    updateMailStatus(true);
    onSelect(email, termsConsent, true);
  };

  const handleMarketingSkip = () => {
    setShowMarketingDialog(false);
    // Save email to checkout data for prefilling checkout form
    updateCheckoutField('email', email);
    updateMailStatus(false);
    onSelect(email, termsConsent, false);
  };

  return (
    <>
      <form className="email-collection" onSubmit={handleSubmit}>
        <div className="email-collection__field">
          <label htmlFor="email" className="email-collection__label">
            {t('emailCollection.emailAddress')} <span className="email-collection__required">*</span>
          </label>
          <input
            type="email"
            id="email"
            className="email-collection__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="m@example.com"
            required
          />
          {emailError && <span className="email-collection__error">{emailError}</span>}
        </div>

        {content.showCheckboxes !== false ? (
          <div className="email-collection__consents">
            <div className="email-collection__consent">
              <input
                type="checkbox"
                id="terms"
                checked={termsConsent}
                onChange={(e) => setTermsConsent(e.target.checked)}
                required
              />
              <label htmlFor="terms">
                {t('emailCollection.termsAgreement')}
                {' '}
              <a href={'/terms'} target="_blank" rel="noopener noreferrer">{t('emailCollection.termsAgreementLinkFirst')}</a>
              {' '}
              {t('emailCollection.termsAgreementSeparator')}
              {' '}
              <a href={'/privacy'} target="_blank" rel="noopener noreferrer">{t('emailCollection.termsAgreementLinkSecond')}</a>
                {' '}
                <span className="email-collection__required">*</span>
              </label>
            </div>

            <div className="email-collection__consent">
              <input
                type="checkbox"
                id="marketing"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
              />
              <label htmlFor="marketing">
                {t('emailCollection.marketingConsent')}
              </label>
            </div>
          </div>
        ) : (
          <div className="email-collection__privacy-notice">
            <p className="email-collection__privacy-text">
              {t('emailCollection.privacyNotice')} {t('emailCollection.continueAgreement')}
              {' '}
              <a href={'/privacy'} target="_blank" rel="noopener noreferrer">{t('emailCollection.privacyPolicyLink')}</a>
              {' '}
              {t('emailCollection.termsAgreementSeparator')}
              {' '}
              <a href={'/terms'} target="_blank" rel="noopener noreferrer">{t('emailCollection.termsOfUseLink')}</a>.
            </p>
          </div>
        )}
        <div className="inform-selector__button-fixed">
          <button
            type="submit"
            className={`email-collection__submit ${!isFormValid ? 'email-collection__submit--disabled' : ''}`}
            disabled={!isFormValid}
          >
            {t('continueButtonText')}
          </button>
        </div>
      </form>

      <MarketingConsentDialog
        isOpen={showMarketingDialog}
        onClose={handleMarketingSkip}
        onConfirm={handleMarketingConfirm}
      />
    </>
  );
};
